import { Button } from "@fluentui/react-components";
import React, { useMemo } from "react";
import {
  useReminderEntries,
  useRemindersStoreActions,
  useSearchClientsSelectedItem,
} from "store/RemindersStore";
import { ServiceBellRegular } from "@fluentui/react-icons";
import { useAppStore } from "store/AppStore";
import { useTranslation } from "react-i18next";
import {
  useSuggestedReminder,
  useClReminderNumber,
  useClComment,
  useClUnsettledPaymentPlans,
  useClUnsettledPaymentPlanMetadataMap,
  useClGrecoCompanyNumbers,
  useClSearchPayersSelectedItem,
  useCreateReminderDialog,
  useCreateReminderStatus,
  usePopulateCreateReminderDataStatus,
  useRemindersCreateDialogStoreActions,
  useClUseEnglishTemplateIfPossible,
  useClIsPayerReminder,
  useClIsManualPayer,
  useClPayerName,
} from "store/RemindersCreateDialogStore";

const CreateReminderButton = () => {
  const { setRemindersStoreValues, loadReminders } = useRemindersStoreActions();
  const { setRemindersCreateDialogStoreValues } =
    useRemindersCreateDialogStoreActions();

  const { createReminder } = useRemindersCreateDialogStoreActions();
  const [{ clReminderNumber }] = useClReminderNumber();
  const [{ searchClientsSelectedItem }] = useSearchClientsSelectedItem();

  const [{ createReminderDialog }] = useCreateReminderDialog();

  const [{ reminderEntries }] = useReminderEntries();

  const [{ suggestedReminder }] = useSuggestedReminder();

  const [{ clComment }] = useClComment();
  const [{ clUseEnglishTemplateIfPossible }] =
    useClUseEnglishTemplateIfPossible();

  const [{ clUnsettledPaymentPlans }] = useClUnsettledPaymentPlans();

  const [{ createReminderStatus }] = useCreateReminderStatus();

  const [{ populateCreateReminderDataStatus }] =
    usePopulateCreateReminderDataStatus();

  const [{ clUnsettledPaymentPlanMetadataMap }] =
    useClUnsettledPaymentPlanMetadataMap();

  const [{ clGrecoCompanyNumbers }] = useClGrecoCompanyNumbers();

  const [{ clSearchPayersSelectedItem }] = useClSearchPayersSelectedItem();
  const [{ clIsPayerReminder }] = useClIsPayerReminder();
  const [{ clIsManualPayer }] = useClIsManualPayer();
  const [{ clPayerName }] = useClPayerName();
  const [{ updatedGrecoCompanies }] = useAppStore();
  const { t } = useTranslation();

  const isLoading =
    createReminderStatus === "loading" ||
    populateCreateReminderDataStatus === "loading";

  // the payer is either a partner or a manually entered name (a private person without a partner record)
  const payerName = clIsManualPayer ? (clPayerName ?? "").trim() : "";
  const hasPayer = clIsManualPayer
    ? payerName !== ""
    : clSearchPayersSelectedItem !== null;

  const isDisabled =
    isLoading ||
    clUnsettledPaymentPlans?.filter(
      (el) => clUnsettledPaymentPlanMetadataMap[el.paymentPlanEntryId].selected
    ).length === 0 ||
    !hasPayer;

  return (
    <Button
      data-help="reminders-create-confirm"
      appearance="primary"
      disabled={isDisabled}
      icon={<ServiceBellRegular fontSize={16} />}
      onClick={() => {
        const suggestedPayments = clUnsettledPaymentPlans?.filter(
          (el) =>
            clUnsettledPaymentPlanMetadataMap[el.paymentPlanEntryId].selected
        );
        createReminder(
          {
            // a reminder by payer belongs to the payer (its entries keep their own clients)
            clientId: clIsPayerReminder
              ? clSearchPayersSelectedItem.value.id
              : searchClientsSelectedItem.value.id,
            reminder: {
              // optional: left empty, the backend assigns P{yyyyMMdd}-{reminderId}
              ReminderNumber:
                (clReminderNumber ?? "").trim() === ""
                  ? null
                  : clReminderNumber.trim(),
              DueDate: suggestedReminder.dueDate,
              ReminderComment: clComment,
              CurrencyCode: suggestedReminder.currencyCode,
              IsBrokerPremiumCollectionReminder:
                suggestedReminder?.isBrokerPremiumCollectionReminder,
              SuggestedPayments: suggestedPayments,
              GrecoCompanyNumber: clGrecoCompanyNumbers[0],
              UseEnglishTemplateIfPossible: clUseEnglishTemplateIfPossible,
              payerClientId: clIsManualPayer
                ? null
                : clSearchPayersSelectedItem.value.id,
              payerName: clIsManualPayer ? payerName : null,
              IsPayerReminder: clIsPayerReminder,
            },
          },
          () => {
            setRemindersCreateDialogStoreValues({
              createReminderDialog: {
                isOpen: false,
                ReminderEntry: null,
                reminder: null,
              },
              clIsManualPayer: false,
              clPayerName: "",
              clInsurersCountryCode: null,
              clUnsettledPaymentPlans: [],
              clUnsettledPaymentPlanMetadataMap: {},
              clGrecoCompanyNumbers: [],
              clTotal: [0, 0],
              clReminder: null,
              clInsurersSelectedItem: null,
              clSearchTerm: "",
              clComment: "",
              clUseEnglishTemplateIfPossible: false,
              clServiceSegmentCode: null,
            });
            setRemindersStoreValues({
              reminderEntries: reminderEntries.map((el) => {
                if (el.reminderEntryId === createReminderDialog.reminderId) {
                  return {
                    ...el,
                    isCleared: true,
                  };
                }
                return el;
              }),
            });
            loadReminders({
              reset: true,
              delay: true,
              updatedGrecoCompanies,
            });
          }
        );
      }}
    >
      {t("pct.createReminder.label")}
    </Button>
  );
};
export default CreateReminderButton;
